<template>
  <button
    :class="[
      this.$style['application-step'],
      isDisabled && this.$style['application-step--disabled'],
    ]"
    :data-cy="kebabCase(title)"
    @click="handleClick"
  >
    <div
      v-if="!isError && !isWarning && !isComplete && stepCount"
      :class="$style['step-count']"
    >
      {{ stepCount }}
    </div>
    <div
      v-else-if="!!statusIconPath"
      :class="$style['status-icon']"
      data-cy="warning-icon"
      v-html="statusIconPath"
    ></div>
    <div :class="$style['wrapper']">
      <p :class="$style.title">{{ title }}</p>
      <Tag
        v-if="actionRequired"
        :class="$style.actionTag"
        severity="warning"
        value="Action Required"
      ></Tag>
    </div>
    <LockOutbound v-if="isLocked" :class="$style.icon" />
    <Chevron v-else :class="$style.chevron" />
  </button>
</template>

<script>
import Chevron from '@/assets/icons/next-step.svg';
import Check from '@/assets/icons/check.svg?raw';
import Error from '@/assets/icons/error.svg?raw';
import Warning from '@/assets/icons/warning-info.svg?raw';
import LockOutbound from '@/assets/icons/lock-outbound-v2.svg';
import kebabCase from '@/utils/kebabcase';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import {
  sendSegmentTrackEvent,
  formatStepLabelForSegment,
} from '@/utils/outbound-onboarding-v2';
import Tag from 'primevue/tag';
import { RouteNames } from '@/data/routes';

const STEP_STATES = {
  COMPLETE: 'complete',
  INCOMPLETE: 'incomplete',
  ERROR: 'error',
  WARNING: 'warning',
};

export default {
  components: { Tag, Chevron, LockOutbound },
  props: {
    title: {
      type: String,
      required: true,
    },
    state: {
      type: String,
      required: true,
      validator: (state) => Object.values(STEP_STATES).includes(state),
    },
    destination: {
      type: String,
      required: true,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    stepCount: {
      type: Number,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    actionRequired: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isComplete() {
      return this.state === STEP_STATES.COMPLETE;
    },
    isError() {
      return this.state === STEP_STATES.ERROR;
    },
    isWarning() {
      return this.state === STEP_STATES.WARNING;
    },
    statusIconPath() {
      if (this.isError) {
        return Error;
      }
      if (this.isWarning) {
        return Warning;
      }
      if (this.isComplete) {
        return Check;
      }
      return undefined;
    },
  },
  methods: {
    kebabCase,
    handleClick() {
      if (this.isDisabled) return;

      sendSegmentTrackEvent(this.$route.name, 'btn_click', {
        element: 'module-btn',
        module: formatStepLabelForSegment(this.title, false),
        blocked: this.isLocked,
      });

      if (this.destination === RouteNames.OWNERS.INDEX) {
        analytics.track(SEGMENT_EVENTS.fe_business_owner_profiles_clicked);
      }

      if (!this.isLocked) {
        this.$router.push({
          name: this.destination,
        });
      } else {
        this.$emit('stepLocked');
      }
    },
  },
};
</script>

<style lang="less" module>
.application-step {
  all: unset;
  box-sizing: border-box;
  width: 100%;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  border: 1px solid #ceced0;
  border-radius: 4px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  gap: 1rem;
  padding: 1rem 1.5rem;

  .step-count {
    display: flex;
    color: @color-lightgray-300;
    border: 1px solid @color-lightgray-300;
    border-radius: 50%;
    width: 1rem;
    height: 1rem;
    font-size: 12px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    margin-top: 5px;
  }

  .wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
    gap: 6px;

    .title {
      font-family: @regular-font;
      font-weight: 300;
      font-size: 1rem;
      line-height: 1.5rem;
      color: #000000;
      text-align: left;
      margin: 0;
    }

    .actionTag {
      text-align: center;
    }
  }

  & + .application-step,
  & + [class^='p-button'] {
    margin-top: 1rem;
  }

  @media only screen and (min-width: 960px) {
    & + .application-step,
    & + [class^='p-button'] {
      margin: 1.5rem 0 0;
    }
  }

  .chevron {
    margin-left: 10px;
    width: 1rem;
    height: 1rem;
    -webkit-transition: all 300ms ease;
    transition: all 300ms ease;
  }

  .status-icon {
    display: inline-flex;
  }
}

.application-step:focus {
  outline: none;
}

.application-step:hover {
  .chevron {
    transform: translateX(0.75rem);
  }
}

.application-step--disabled {
  cursor: default;
  background-color: #f8f8fa;
  opacity: 0.5;

  .title {
    color: #ababad;
  }
}

.application-step--disabled:hover {
  .chevron {
    transform: unset;
  }
}

.icon {
  display: contents;

  svg {
    width: 16px;
    height: 16px;
    margin: 10px;
  }
}
</style>
